import { InteractionType } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { AuthenticationProvider } from "@tql/auth-react";
import { useAtom } from "jotai";
import { useEffect } from "react";

import { createPublicRouter, createRouter } from "~/app/routes/web-router.tsx";
import { Feedback } from "~/features/feedback/index.tsx";
import { useGetActiveCustomerProfile } from "~/features/profile/hooks/use-get-active-customer-profile.ts";
import { useGetUserCustomers } from "~/features/profile/hooks/use-get-user-customers.ts";
import { useGetUserPreferences } from "~/features/profile/hooks/use-get-user-preferences.ts";
import { Construction } from "~/shared/components/construction/construction.tsx";
import { DatadogRouterProvider } from "~/shared/components/datadog-router-provider.tsx";
import { FeatureFlagsProvider } from "~/shared/components/feature-flags/feature-flags-provider.tsx";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { NotificationProvider } from "~/shared/components/notification-provider/notification-provider.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import { B2CAuthProvider } from "~/shared/utils/b2c-auth.tsx";

import { loginRequest, msalInstance } from "./auth-config.ts";
import {
	getAccountEmail,
	resolveInitialCustomer,
} from "./resolve-initial-customer.ts";
import { UserLoginEventsTracker } from "./user-login-events-tracker.tsx";

function LaunchDarklyWrapper({
	children,
}: Readonly<{ children: React.ReactNode }>) {
	const { accounts } = useMsal();
	const { isInternal } = useDeploymentType();
	const [selectedCustomer] = useAtom(selectedCustomerAtom);
	const account = accounts[0];

	let ldContextKey: string | undefined;
	let userAttributes: Record<string, unknown> = {};
	const kind = isInternal ? "user" : "customer";

	if (account) {
		if (isInternal) {
			// Internal users: use localAccountId as key
			ldContextKey = account.localAccountId;
			userAttributes = {
				name: account.name,
				email: account.username,
			};
		} else {
			// External B2C users: use customer ID from selectedCustomerAtom
			ldContextKey = selectedCustomer?.id
				? String(selectedCustomer.id)
				: "anonymous";
			userAttributes = {
				name: account.name,
				email: account.idTokenClaims?.["email"],
				cid: selectedCustomer?.id ? String(selectedCustomer.id) : "none",
			};
		}
	} else {
		ldContextKey = "anonymous";
	}

	return (
		<FeatureFlagsProvider
			contextKind={kind}
			userAttributes={userAttributes}
			userKey={ldContextKey}
		>
			{children}
		</FeatureFlagsProvider>
	);
}

// Component that initializes customer data for B2C external users
function CustomerDataInitializer({
	children,
}: Readonly<{ children: React.ReactNode }>) {
	const { accounts } = useMsal();
	// Prefetch user preferences up front so the cache is warm by the time any
	// grid mounts. We only consume `isLoading` here — the data is read by
	// individual feature hooks (e.g. `useGridPreferencesSync`).
	const { isLoading: isUserPreferencesLoading } = useGetUserPreferences();
	const {
		data: userCustomers,
		error: userCustomersError,
		isLoading: isUserCustomersLoading,
	} = useGetUserCustomers();
	// The active customer profile is persisted server-side so it survives logout
	// and is restored on the next login. The server applies access-aware
	// fallbacks (single-profile auto-selection and removed-profile recovery), so
	// the returned id is always one the user can access (or `0` when they have
	// none). The endpoint is user-scoped via the JWT (no customer header
	// needed), so it can fire on app boot before any customer is selected.
	const {
		data: activeProfile,
		error: activeProfileError,
		isLoading: isActiveProfileLoading,
	} = useGetActiveCustomerProfile();
	const [selectedCustomer, setSelectedCustomer] = useAtom(selectedCustomerAtom);
	const accountEmail = getAccountEmail(accounts[0]);

	// Wait for both the customer list and the persisted profile, then set
	// `selectedCustomer` exactly once. This ordering matters: `useIsVendor`
	// (and any role-aware hook) is per-customer, so firing
	// `useGetCurrentUserInfo` against `userCustomers[0]` first \u2014 then swapping
	// to the persisted customer \u2014 caches the wrong role for users whose
	// persisted customer differs from their first accessible customer (e.g. an
	// admin on customer A and a vendor on customer B). By gating role lookup on
	// the persisted customer being selected first, the role we get back is the
	// role for the customer the user actually has active.
	//
	// `@tql.com` users default to `TQL_DEFAULT_CUSTOMER_ID` when it is in their
	// accessible list, overriding the persisted profile (see
	// `resolveInitialCustomer`). The cached `selectedCustomer` is cleared on
	// login (see `clearSelectedCustomer` in `b2c-utils.ts`) so this default is
	// always re-evaluated for a fresh session — a same-tab re-login won't keep a
	// previously selected non-default profile.
	useEffect(() => {
		if (selectedCustomer) return;
		if (isUserCustomersLoading || isActiveProfileLoading) return;
		if (userCustomersError || !userCustomers?.length) return;

		const nextCustomer = resolveInitialCustomer({
			accountEmail,
			userCustomers,
			activeProfile,
			activeProfileError,
		});
		if (nextCustomer) {
			setSelectedCustomer(nextCustomer);
		}
	}, [
		accountEmail,
		selectedCustomer,
		userCustomers,
		userCustomersError,
		isUserCustomersLoading,
		activeProfile,
		activeProfileError,
		isActiveProfileLoading,
		setSelectedCustomer,
	]);

	// Note: both hooks set `refetchOnMount: false`, so once the initial fetch
	// resolves (data OR error) `isLoading` will not flip back to true when
	// descendants subscribe. That avoids the remount/refetch loop that
	// previously caused page flicker for users whose queries 403.
	if (
		isUserPreferencesLoading ||
		isUserCustomersLoading ||
		isActiveProfileLoading
	) {
		return <LoadingSpinner />;
	}

	return children;
}

// Component that handles feature flag gating for B2C external users
function B2CFeatureGate({ children }: Readonly<{ children: React.ReactNode }>) {
	const { isReady, isEnabled } = useFeatureFlagGate("traxLtlBetaUsers");

	// Show loading while waiting for LD initialization
	if (!isReady) {
		return <LoadingSpinner />;
	}

	if (!isEnabled) {
		return (
			<Construction
				message={
					"This Application is currently in development and will be available soon."
				}
				routingEnabled={false}
			/>
		);
	}

	return children;
}

// Consolidates authenticated UI components shared across internal and external deployments
function AuthenticatedContent({
	router,
}: Readonly<{
	router: ReturnType<typeof createRouter>;
}>) {
	const { isInternal } = useDeploymentType();

	const content = (
		<>
			<DatadogRouterProvider router={router} />
			<Feedback />
			<NotificationProvider />
		</>
	);

	return (
		<LaunchDarklyWrapper>
			{/* External-only login-events tracker. Rendered inside the LD provider
			    so it can read the `logUserLoginEvents` flag, and after
			    `CustomerDataInitializer` so the customer header is available. */}
			{!isInternal && <UserLoginEventsTracker />}
			{isInternal ? content : <B2CFeatureGate>{content}</B2CFeatureGate>}
		</LaunchDarklyWrapper>
	);
}

export function App() {
	const authenticatedRouter = createRouter();
	const publicRouter = createPublicRouter();
	const { isInternal } = useDeploymentType();

	const unauthenticated = (
		<>
			<DatadogRouterProvider router={publicRouter} />
			<NotificationProvider />
		</>
	);

	if (isInternal) {
		return (
			<AuthenticationProvider
				apiTokenRequest={{
					scopes: loginRequest.scopes,
				}}
				instance={msalInstance}
				interactionType={InteractionType.None}
				loading={<LoadingSpinner />}
				uiAuthenticationRequest={{
					scopes: loginRequest.scopes,
				}}
				unauthenticated={unauthenticated}
			>
				<AuthenticatedContent router={authenticatedRouter} />
			</AuthenticationProvider>
		);
	}

	return (
		<B2CAuthProvider unauthenticated={unauthenticated}>
			<CustomerDataInitializer>
				<AuthenticatedContent router={authenticatedRouter} />
			</CustomerDataInitializer>
		</B2CAuthProvider>
	);
}
